import { CGU_VERSIONS, versionEnVigueur, type TexteVersionCgu } from '@artosera/shared';
import { data, redirect, type LoaderFunctionArgs } from 'react-router';
import { estSessionIndisponible } from '../../api/errors.js';
import type { SessionUtilisateur } from '../../api/types.js';
import { adresseDeRetour, retourSur } from '../../auth/retour.js';
import { chargerSession } from '../../auth/session.js';
import { chargerTexteCgu } from '../../cgu/textes.js';
import { RAISON_HORS_LIGNE } from '../loaders.js';

export interface DonneesConditions {
  readonly session: SessionUtilisateur;
  /** Où revenir après l'acceptation : un chemin de la régie, assaini (`auth/retour.ts`). */
  readonly retour: string;
  /** La version affichée — à accepter, acceptée, ou en vigueur —, `null` : aucune. */
  readonly version: string | null;
  /** Son texte, `null` quand cette régie ne le porte pas. */
  readonly texte: TexteVersionCgu | null;
}

/**
 * `/app/conditions` (ARTO-59) : la session est exigée et TOUJOURS relue — c'est
 * elle qui dit s'il faut accepter, et quelle version. Aucun repli hors ligne :
 * on n'accepte pas sans le serveur, et une consultation hors ligne n'a rien à
 * prouver ici.
 */
export async function conditionsLoader({
  request,
}: LoaderFunctionArgs): Promise<DonneesConditions> {
  const retour = retourSur(new URL(request.url).searchParams.get('retour'));
  let session: SessionUtilisateur;
  try {
    session = await chargerSession(null);
  } catch (erreur) {
    if (estSessionIndisponible(erreur)) throw data({ raison: RAISON_HORS_LIGNE }, { status: 503 });
    throw redirect(`/connexion?retour=${encodeURIComponent(adresseDeRetour(request.url))}`);
  }
  const version =
    session.cguAcceptationRequise?.version ??
    session.cgu?.version ??
    versionEnVigueur(CGU_VERSIONS, new Date())?.version ??
    null;
  return { session, retour, version, texte: version ? await chargerTexteCgu(version) : null };
}
